import React from 'react';
import {
    Alert, Button, Checkbox, Collapse, Input, Popconfirm, Progress,
    Select, Space, Switch, Table, Tag, Typography, message
} from 'antd';
import { CaretRightOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { anonApi } from '../util/anonymizerApi';

const TYPE_OPTIONS = [
    { label: 'Persons', value: 'person' },
    { label: 'Text (random)', value: 'text' },
    { label: 'Text (ML / remove PII)', value: 'mlText' },
    { label: 'Delete records', value: 'delete' }
];

const STATUS_COLORS = {
    running: 'processing',
    finished: 'success',
    finished_with_errors: 'warning',
    failed: 'error'
};

function formatDuration(run) {
    if (!run || !run.startedAt) return '';
    const end = run.finishedAt ? new Date(run.finishedAt) : new Date();
    const seconds = Math.round((end - new Date(run.startedAt)) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function sumStats(run, key) {
    if (!run || !run.stats) return 0;
    return Object.keys(run.stats).reduce((acc, type) => acc + (run.stats[type][key] || 0), 0);
}

const { Text } = Typography;

// ---- human-friendly schedule <-> cron ----
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function parseCron(cron) {
    const p = String(cron || '').trim().split(/\s+/);
    if (p.length !== 5) return { freq: 'custom' };
    const [m, h, dom, mon, dow] = p;
    const num = (x) => /^\d+$/.test(x);
    const time = (num(h) && num(m)) ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` : '02:00';
    if (m === '0' && h === '*' && dom === '*' && mon === '*' && dow === '*') return { freq: 'hourly', time: '00:00' };
    if (num(h) && num(m) && dom === '*' && mon === '*' && dow === '*') return { freq: 'daily', time };
    if (num(h) && num(m) && dom === '*' && mon === '*' && num(dow)) return { freq: 'weekly', time, dow };
    if (num(h) && num(m) && num(dom) && mon === '*' && dow === '*') return { freq: 'monthly', time, dom };
    return { freq: 'custom' };
}
function buildCron(freq, time, dow, dom) {
    const [h, m] = String(time || '02:00').split(':').map((s) => parseInt(s, 10) || 0);
    if (freq === 'hourly') return '0 * * * *';
    if (freq === 'daily') return `${m} ${h} * * *`;
    if (freq === 'weekly') return `${m} ${h} * * ${dow || 1}`;
    if (freq === 'monthly') return `${m} ${h} ${dom || 1} * *`;
    return null;
}
function humanCron(cron) {
    const c = parseCron(cron);
    if (c.freq === 'hourly') return 'Runs every hour, on the hour.';
    if (c.freq === 'daily') return `Runs every day at ${c.time}.`;
    if (c.freq === 'weekly') return `Runs every ${DOW[parseInt(c.dow, 10)] || 'week'} at ${c.time}.`;
    if (c.freq === 'monthly') return `Runs on day ${c.dom} of every month at ${c.time}.`;
    return `Custom schedule (cron: ${cron || '—'}).`;
}

const ChangeItem = ({ change }) => {
    if (change.action === 'delete') {
        return (
            <div style={{ marginBottom: 8 }}>
                <Tag color="red">delete</Tag>
                <b>{change.form}</b> — {change.count} record{change.count === 1 ? '' : 's'}
                {change.description && <> ({change.description})</>}
                <div><code style={{ fontSize: 12, wordBreak: 'break-all' }}>{change.query}</code></div>
            </div>
        );
    }
    return (
        <div style={{ marginBottom: 8 }}>
            <Tag color="blue">update</Tag>
            <b>{change.form}</b>
            {change.description && <> ({change.description})</>}
            {change.recordId && <> — <code>{change.recordId}</code></>}
            {change.query && (
                <div><code style={{ fontSize: 12, wordBreak: 'break-all' }}>{change.query}</code></div>
            )}
            <div style={{ paddingLeft: 12 }}>
                {change.fields &&
                    Object.keys(change.fields).map((f) => (
                        <div key={f} style={{ fontSize: 13 }}>
                            <b>{f}</b>:{' '}
                            <Text delete type="secondary">
                                {String(change.fields[f].old === null || change.fields[f].old === undefined ? '(empty)' : change.fields[f].old).substring(0, 120)}
                            </Text>{' '}
                            → <Text type="success">{String(change.fields[f].new).substring(0, 200)}</Text>
                        </div>
                    ))}
                {change.values &&
                    Object.keys(change.values).map((f) => (
                        <div key={f} style={{ fontSize: 13 }}>
                            <b>{f}</b> → <Text type="success">{String(change.values[f]).substring(0, 200)}</Text>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const RunDetails = ({ run }) => {
    const steps = Object.keys(run.stats || {}).map((type) => ({ type, ...run.stats[type] }));
    const allErrors = steps.reduce(
        (acc, s) => acc.concat((s.errors || []).map((e) => ({ step: s.type, ...e }))),
        []
    );
    const would = run.dryRun ? 'Would ' : '';
    const optionalColumns = [
        { title: `${would}Update${run.dryRun ? '' : 'd'}`, dataIndex: 'recordsUpdated', key: 'updated' },
        { title: `${would}${run.dryRun ? 'update related' : 'Related updates'}`, dataIndex: 'relatedUpdates', key: 'related' },
        { title: `${would}Delete${run.dryRun ? '' : 'd'}`, dataIndex: 'recordsDeleted', key: 'deleted' },
        { title: 'ML calls', dataIndex: 'mlCalls', key: 'ml' }
    ].filter((col) => steps.some((s) => (s[col.dataIndex] || 0) > 0));

    return (
        <>
            {allErrors.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${allErrors.length} error${allErrors.length > 1 ? 's' : ''} in this run`}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {allErrors.map((e, i) => (
                                <li key={i} style={{ marginBottom: 4 }}>
                                    <Tag>{e.step}</Tag>
                                    {e.form && <b>{e.form}: </b>}
                                    {e.message}
                                    {e.recordId && (
                                        <div>
                                            <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                                {e.recordId}
                                            </code>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    }
                />
            )}
            {run.dryRun && steps.some((s) => (s.changes || []).length > 0) && (
                <Collapse style={{ marginBottom: 12 }}>
                    {steps
                        .filter((s) => (s.changes || []).length > 0)
                        .map((s) => (
                            <Collapse.Panel
                                key={s.type}
                                header={`Planned changes — ${s.type} (${s.changes.length}${s.changesTruncated ? ` shown, ${s.changesTruncated} more` : ''})`}
                            >
                                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                                    {s.changes.map((c, i) => (
                                        <ChangeItem key={i} change={c} />
                                    ))}
                                </div>
                            </Collapse.Panel>
                        ))}
                </Collapse>
            )}
            <Table
                size="small"
                pagination={false}
                rowKey="type"
                dataSource={steps}
                columns={[
                    { title: 'Step', dataIndex: 'type', key: 'type' },
                    { title: 'Found', dataIndex: 'recordsFound', key: 'found' },
                    { title: 'Processed', dataIndex: 'recordsProcessed', key: 'processed' },
                    ...optionalColumns,
                    {
                        title: 'Duration',
                        dataIndex: 'durationMs',
                        key: 'duration',
                        render: (ms) => (ms ? `${Math.round(ms / 1000)}s` : '-')
                    },
                    {
                        title: 'Errors',
                        dataIndex: 'errors',
                        key: 'errors',
                        render: (errors) => (
                            <Tag color={errors && errors.length ? 'error' : 'success'}>
                                {(errors || []).length}
                            </Tag>
                        )
                    }
                ]}
            />
        </>
    );
};

const cap = { fontSize: 12, letterSpacing: '.085em', textTransform: 'uppercase', color: 'var(--sc-ink-3)', fontWeight: 700 };

const RunsPanel = () => {
    const [status, setStatus] = React.useState(null);
    const [runs, setRuns] = React.useState([]);
    const [schedule, setSchedule] = React.useState(null);
    const [selectedTypes, setSelectedTypes] = React.useState(TYPE_OPTIONS.map((o) => o.value));
    const [dryRun, setDryRun] = React.useState(true);
    const [starting, setStarting] = React.useState(false);
    const [savingSchedule, setSavingSchedule] = React.useState(false);
    // "Custom (cron)" can't be represented by any concrete cron (every valid
    // expression round-trips to a named frequency), so remember it explicitly
    const [customCron, setCustomCron] = React.useState(false);

    const refresh = React.useCallback(() => {
        anonApi('/v1/status')
            .then((s) => {
                setStatus(s);
                if (!schedule) setSchedule(s.schedule);
            })
            .catch(() => {});
        anonApi('/v1/runs').then((r) => setRuns(Array.isArray(r) ? r : [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedule]);

    React.useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 3000);
        return () => clearInterval(interval);
    }, [refresh]);

    const startRun = () => {
        setStarting(true);
        anonApi('/v1/runs', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ types: selectedTypes, dryRun })
        })
            .then(() => {
                message.success(dryRun ? 'Dry run started' : 'Anonymization run started');
                refresh();
            })
            .catch((e) => message.error(e.message))
            .finally(() => setStarting(false));
    };

    const saveSchedule = () => {
        setSavingSchedule(true);
        anonApi('/v1/schedule', {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(schedule)
        })
            .then((s) => {
                setSchedule(s);
                message.success('Schedule saved');
            })
            .catch((e) => message.error(e.message))
            .finally(() => setSavingSchedule(false));
    };

    const current = status && status.currentRun;
    const last = status && status.lastRun;

    return (
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
            <div className="sc-sechead">
                <h2>Runs</h2>
                <p>Start a dry run or a real run, follow progress, and schedule recurring runs.</p>
            </div>

            <div className="sc-grid2" style={{ marginBottom: 16 }}>
                {/* ---- Current status ---- */}
                <div className="sc-panel">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: current ? 'var(--sc-brand)' : 'var(--sc-ink-3)', display: 'inline-block' }} />
                        <span style={cap}>Current status</span>
                        <span style={{ marginLeft: 'auto' }} />
                        <Button size="small" icon={<ReloadOutlined />} onClick={refresh} />
                    </div>

                    {current ? (
                        <>
                            <Progress percent={(current.progress && current.progress.percent) || 0} status="active" />
                            <p style={{ color: 'var(--sc-ink-2)' }}>
                                Running since {new Date(current.startedAt).toLocaleTimeString()} ({formatDuration(current)})
                                {current.progress && current.progress.currentType && (<> — current step: <b>{current.progress.currentType}</b></>)}
                                {current.dryRun && <Tag style={{ marginLeft: 8 }}>dry run</Tag>}
                            </p>
                        </>
                    ) : (
                        <>
                            <p style={{ margin: '0 0 8px', color: 'var(--sc-ink)', fontWeight: 500 }}>No run in progress.</p>
                            {last ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13.5, color: 'var(--sc-ink-2)' }}>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>Last run</span>
                                    <Tag color={STATUS_COLORS[last.status]} style={{ marginInlineEnd: 0 }}>{last.status.replace(/_/g, ' ')}</Tag>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>·</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{new Date(last.startedAt).toLocaleString()}</span>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>·</span>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>{formatDuration(last)}, {last.trigger}{last.dryRun ? ', dry run' : ''}</span>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>·</span>
                                    <span><b style={{ fontWeight: 600, color: 'var(--sc-ink)' }}>{sumStats(last, 'recordsFound')}</b> found</span>
                                    <span style={{ color: 'var(--sc-ink-3)' }}>·</span>
                                    <span>{last.dryRun ? 'would update' : 'updated'} {sumStats(last, 'recordsUpdated')}{sumStats(last, 'relatedUpdates') > 0 ? ` (+${sumStats(last, 'relatedUpdates')} rel.)` : ''}</span>
                                    {sumStats(last, 'recordsDeleted') > 0 && (<><span style={{ color: 'var(--sc-ink-3)' }}>·</span><span>{last.dryRun ? 'would delete' : 'deleted'} {sumStats(last, 'recordsDeleted')}</span></>)}
                                </div>
                            ) : (
                                <p style={{ margin: 0, color: 'var(--sc-ink-3)' }}>No previous runs.</p>
                            )}
                        </>
                    )}

                    <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <Checkbox.Group options={TYPE_OPTIONS} value={selectedTypes} onChange={setSelectedTypes} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <Switch checked={dryRun} onChange={setDryRun} />
                            <span style={{ color: 'var(--sc-ink-2)', fontSize: 13.5 }}>Dry run — no changes in the target systems</span>
                            <span style={{ marginLeft: 'auto' }} />
                            <Popconfirm
                                title="Start anonymization run?"
                                description={dryRun ? 'Dry run: nothing will be changed.' : 'This will modify and delete data in the target systems!'}
                                onConfirm={startRun}
                                okText="Start"
                            >
                                <Button type="primary" icon={<CaretRightOutlined />} loading={starting} disabled={!!current || selectedTypes.length === 0}>
                                    Start run
                                </Button>
                            </Popconfirm>
                        </div>
                    </div>
                </div>

                {/* ---- Schedule ---- */}
                <div className="sc-panel">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <ClockCircleOutlined style={{ color: 'var(--sc-ink-3)' }} />
                        <span style={cap}>Schedule</span>
                    </div>
                    {schedule && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Switch checked={schedule.enabled} onChange={(enabled) => setSchedule({ ...schedule, enabled })} />
                                <span style={{ color: 'var(--sc-ink-2)', fontSize: 13.5 }}>Scheduling {schedule.enabled ? 'enabled' : 'disabled'}</span>
                            </div>
                            {(() => {
                                const c = parseCron(schedule.cron);
                                // stay in custom mode either because the user chose it, or
                                // because a saved cron doesn't map to a named frequency
                                const isCustom = customCron || c.freq === 'custom';
                                const timeStyle = { background: 'var(--sc-surface)', border: '1px solid var(--sc-border-strong)', borderRadius: 10, color: 'var(--sc-ink)', padding: '8px 11px', fontSize: 14, colorScheme: 'dark' };
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                            <div className="sc-field" style={{ flex: 1, minWidth: 160 }}>
                                                <label>Frequency</label>
                                                <Select
                                                    value={isCustom ? 'custom' : c.freq}
                                                    onChange={(freq) => {
                                                        if (freq === 'custom') { setCustomCron(true); return; }
                                                        setCustomCron(false);
                                                        const nc = buildCron(freq, c.time, c.dow, c.dom);
                                                        if (nc) setSchedule({ ...schedule, cron: nc });
                                                    }}
                                                    options={[
                                                        { value: 'daily', label: 'Every day' },
                                                        { value: 'weekly', label: 'Every week (Mon)' },
                                                        { value: 'monthly', label: 'Every month (1st)' },
                                                        { value: 'hourly', label: 'Every hour' },
                                                        { value: 'custom', label: 'Custom (cron)' }
                                                    ]}
                                                />
                                            </div>
                                            {!isCustom && (c.freq === 'daily' || c.freq === 'weekly' || c.freq === 'monthly') && (
                                                <div className="sc-field" style={{ width: 130 }}>
                                                    <label>Time</label>
                                                    <input type="time" value={c.time || '02:00'} style={timeStyle}
                                                        onChange={(e) => setSchedule({ ...schedule, cron: buildCron(c.freq, e.target.value, c.dow, c.dom) })} />
                                                </div>
                                            )}
                                        </div>
                                        {isCustom && (
                                            <Input addonBefore="Cron" value={schedule.cron} onChange={(e) => setSchedule({ ...schedule, cron: e.target.value })} placeholder="0 2 * * *" />
                                        )}
                                        <span style={{ fontSize: 13, color: 'var(--sc-ink-2)' }}>
                                            {isCustom ? 'Custom schedule — enter a five-field cron expression above (min hour day month weekday).' : humanCron(schedule.cron)}
                                        </span>
                                    </div>
                                );
                            })()}
                            <Checkbox.Group
                                options={TYPE_OPTIONS}
                                value={schedule.types && schedule.types.length ? schedule.types : TYPE_OPTIONS.map((o) => o.value)}
                                onChange={(types) => setSchedule({ ...schedule, types })}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Switch checked={schedule.dryRun} onChange={(v) => setSchedule({ ...schedule, dryRun: v })} />
                                <span style={{ color: 'var(--sc-ink-2)', fontSize: 13.5 }}>Scheduled runs as dry run</span>
                            </div>
                            <div>
                                <Button type="primary" loading={savingSchedule} onClick={saveSchedule}>Save schedule</Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ---- Run history ---- */}
            <div className="sc-panel pad0">
                <div style={{ padding: '18px 22px 4px' }}><span style={cap}>Run history</span></div>
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={runs}
                    style={{ padding: '0 8px 8px' }}
                    expandable={{ expandedRowRender: (run) => <RunDetails run={run} />, expandRowByClick: true }}
                    columns={[
                        { title: 'Started', dataIndex: 'startedAt', key: 'startedAt', render: (v) => new Date(v).toLocaleString() },
                        {
                            title: 'Status', dataIndex: 'status', key: 'status',
                            render: (v, run) => (
                                <Space>
                                    <Tag color={STATUS_COLORS[v]}>{v.replace(/_/g, ' ')}</Tag>
                                    {run.dryRun && <Tag>dry run</Tag>}
                                    {run.trigger === 'schedule' && <Tag icon={<ClockCircleOutlined />}>scheduled</Tag>}
                                </Space>
                            )
                        },
                        { title: 'Duration', key: 'duration', render: (_, run) => formatDuration(run) },
                        {
                            title: 'Records', key: 'records',
                            render: (_, run) => {
                                const updated = sumStats(run, 'recordsUpdated');
                                const related = sumStats(run, 'relatedUpdates');
                                const deleted = sumStats(run, 'recordsDeleted');
                                const would = run.dryRun ? 'would ' : '';
                                const parts = [`${sumStats(run, 'recordsFound')} found`];
                                if (updated || related) {
                                    parts.push(`${would}update${run.dryRun ? '' : 'd'} ${updated}` + (related ? ` (+${related} related)` : ''));
                                }
                                if (deleted) parts.push(`${would}delete${run.dryRun ? '' : 'd'} ${deleted}`);
                                return parts.join(' · ');
                            }
                        },
                        {
                            title: 'Errors', key: 'errors',
                            render: (_, run) => {
                                const count = Object.keys(run.stats || {}).reduce((acc, t) => acc + ((run.stats[t].errors || []).length), 0);
                                return <Tag color={count ? 'error' : 'success'}>{count}</Tag>;
                            }
                        }
                    ]}
                />
            </div>
        </div>
    );
};

export default RunsPanel;
